import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Autocomplete, Link, SxProps, TextField, Theme } from "@mui/material";
import { PartnerType } from "types/partners/PartnerTypes";
import { usePartners } from "hooks/usePartners";
import { inquiryTypeLabel } from "pages/outsourcing_management/constants/inquiryStatusColors";

// One label for every partner/supplier Autocomplete: company first, then the
// contact person, and the raw id for the handful of partners that carry
// neither.
export const getSupplierLabel = (partner: PartnerType) =>
  partner.info?.company ||
  `${partner.info?.firstName ?? ""} ${partner.info?.lastName ?? ""}`.trim() ||
  `#${partner.id}`;

// Alphabetical by what the dropdown shows, ignoring case. Danish collation,
// so Æ, Ø and Å come after Z - but Danish also files every "Aa" under Å, at
// the very end, which hides "AA Plast" and "Aarhus". A combining grapheme
// joiner between the two a's is ignored by the comparison yet breaks that
// pairing, so "aa" sorts as two plain a's.
const keepAaApart = (name: string) => name.replace(/a(?=a)/gi, "$&\u034f");

export const compareSupplierNames = (a: string, b: string) =>
  keepAaApart(a).localeCompare(keepAaApart(b), "da", { sensitivity: "base" });

// NFD folds the accented latin letters, but ae/oe/aa are separate letters in
// Danish rather than accents, so half our supplier names ("Vaerloese
// Galvaniske") stay unreachable from a keyboard without them unless we spell
// them out here.
const normalize = (value: string) =>
  value
    .toLowerCase()
    // Before the NFD pass: it decomposes "\u00e5" into a + ring and the strip
    // below would leave a bare "a".
    .normalize("NFC")
    .replace(/\u00e6/g, "ae")
    .replace(/\u00f8/g, "oe")
    .replace(/\u00e5/g, "aa")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// MUI's default filter matches the query *anywhere* in the label, so typing a
// single "l" offers Teccluster, JO Plast and every "Test Tools AS" next to
// Linatex - and its `trim: false` default means a trailing space returns
// nothing at all (EE-238). Match on word starts instead, across the company
// and the contact name, so the dropdown offers what the typing looks like.
export const supplierFilterOptions = (
  options: PartnerType[],
  { inputValue }: { inputValue: string }
) => {
  const query = normalize(inputValue.trim());
  if (!query) return options;

  return options.filter((partner) => {
    if (String(partner.id).startsWith(query.replace(/^#/, ""))) return true;

    const haystack = normalize(
      [partner.info?.company, partner.info?.firstName, partner.info?.lastName]
        .filter(Boolean)
        .join(" ")
    );
    // A leading space makes `includes` a word-start test, and keeps
    // multi-word queries ("john jensens") working.
    return haystack.startsWith(query) || haystack.includes(` ${query}`);
  });
};

// useAutocomplete keys each option by `getOptionKey?.(option) ?? label`, and
// the Autocomplete of MUI 5.15 never passes a getOptionKey - so our twelve
// "Test Tools AS" partners all render under the same React key. Reconciliation
// then leaves the dropped duplicates behind as ghost rows: typing "Lin" showed
// Linatex *plus* JO Plast, JB Metalindustri and eleven Test Tools AS (EE-238).
// The partner id is the only key that is actually unique.
export const renderSupplierOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  partner: PartnerType
) => (
  <li {...props} key={partner.id}>
    {getSupplierLabel(partner)}
  </li>
);

// The Autocomplete itself. Five components under Outsourcing each wrote out
// the same nine props - options, value, the three helpers above,
// isOptionEqualToValue, onChange, renderInput - and a sixth would have had to
// remember all of them to behave the same way. It fetches its own list, so a
// caller only says which supplier is picked and what to do when that changes.
type SupplierAutocompleteProps = {
  value: number | undefined;
  onChange: (partnerId: number | undefined) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  /**
   * The service being asked for, as an InquiryTypeEnum value. Given one, the
   * list offers the partners registered for it - PARTNERS.template_type, the
   * single service each partner is on the register as providing - rather than
   * every partner in the system. Requisition types map onto these codes
   * through `inquiryTypeForRequisitionType` in requisitionForm.ts.
   *
   * A soft filter, always: nobody registered for the service means the whole
   * list, the supplier already picked stays on it whatever it is registered
   * for, and the helper text below the input offers "Show all" either way.
   * The register is not complete enough for a dead end to be acceptable.
   *
   * Filter bars pass nothing - searching for an inquiry by its supplier is
   * not the same question as choosing one.
   */
  serviceType?: string;
  /**
   * Pick the single matching supplier when exactly one is registered for
   * `serviceType` and nothing is picked yet. Create forms only: on a form
   * that is editing something, filling in a field the user never touched is
   * an edit they did not ask for.
   */
  autoSelectSingleMatch?: boolean;
};

export const SupplierAutocomplete: FunctionComponent<
  SupplierAutocompleteProps
> = ({
  value,
  onChange,
  label = "Supplier",
  error,
  helperText,
  disabled,
  size = "small",
  sx,
  serviceType,
  autoSelectSingleMatch = false,
}) => {
  const fetched = usePartners();
  // The list arrives in id order, which is creation order - useless for
  // finding a supplier by eye.
  const partners = useMemo(
    () =>
      [...(fetched ?? [])].sort((a, b) =>
        compareSupplierNames(getSupplierLabel(a), getSupplierLabel(b))
      ),
    [fetched]
  );
  // `null`, not `undefined` - an Autocomplete handed `undefined` switches to
  // uncontrolled and stops reflecting later changes to `value`.
  const selected = partners.find((partner) => partner.id === value) ?? null;

  // The escape hatch out of the service filter. Reset whenever the service
  // changes: a different question deserves the filtered answer first.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => setShowAll(false), [serviceType]);

  const matching = useMemo(
    () =>
      serviceType
        ? partners.filter((partner) => partner.templateType === serviceType)
        : partners,
    [partners, serviceType]
  );

  // Nobody registered for this service falls back to the whole list rather
  // than to nothing - see the prop doc.
  const filtered = !!serviceType && !showAll && matching.length > 0;
  const options = useMemo(() => {
    if (!filtered) return partners;
    // The partner already picked stays on the list whatever it is registered
    // for. Drop it and the Autocomplete has no option matching its value, so
    // the input blanks itself on any inquiry whose supplier was chosen before
    // this filter existed. Filtered out of `partners` rather than appended,
    // so it keeps its alphabetical place.
    if (selected && !matching.some((partner) => partner.id === selected.id))
      return partners.filter(
        (partner) =>
          partner.templateType === serviceType || partner.id === selected.id
      );
    return matching;
  }, [filtered, partners, matching, selected, serviceType]);

  const singleMatch =
    filtered && matching.length === 1 ? matching[0] : undefined;
  useEffect(() => {
    if (!autoSelectSingleMatch || value !== undefined || !singleMatch) return;
    onChange(singleMatch.id);
    // onChange is an inline arrow at every call site, so listing it would
    // re-run this on every render of the form above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectSingleMatch, value, singleMatch]);

  const serviceLabel = inquiryTypeLabel(serviceType);
  const toggle = (
    <Link
      component="button"
      type="button"
      variant="caption"
      underline="hover"
      onClick={() => setShowAll((current) => !current)}
      sx={{ verticalAlign: "baseline" }}
    >
      {filtered ? "Show all suppliers" : `Show only ${serviceLabel}`}
    </Link>
  );
  // Said under the input, not in place of the label: the list being short is
  // a fact about the list, and a buyer who does not know why it is short will
  // conclude the supplier is missing.
  const serviceNote = !serviceType ? null : matching.length === 0 ? (
    `No supplier is registered for ${serviceLabel} - showing all.`
  ) : filtered ? (
    <>
      {`Registered for ${serviceLabel} (${matching.length}). `}
      {toggle}
    </>
  ) : (
    <>
      {"Showing all suppliers. "}
      {toggle}
    </>
  );
  // An error is about the value, and it outranks a note about the list.
  const helper =
    error || !serviceNote ? (
      helperText
    ) : helperText ? (
      <>
        {helperText}
        <br />
        {serviceNote}
      </>
    ) : (
      serviceNote
    );

  return (
    <Autocomplete
      size={size}
      sx={sx}
      disabled={disabled}
      options={options}
      value={selected}
      getOptionLabel={getSupplierLabel}
      filterOptions={supplierFilterOptions}
      renderOption={renderSupplierOption}
      isOptionEqualToValue={(option, picked) => option.id === picked.id}
      onChange={(_, partner) => onChange(partner?.id)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helper}
        />
      )}
    />
  );
};
